import parseISO from "date-fns/parseISO"
import PropTypes from "prop-types"
import React, { useContext } from "react"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Panel from "src/components/Panel"
import Tooltip from "src/components/Tooltip"

import {
  createScheduleException,
  updateScheduleException,
} from "src/api/DryStack"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { titlecase } from "src/utils/string_helpers"
import { getCurrentMarinaSlug } from "src/utils/url/parsing/marina"

import { DryStackExceptionsContext } from "./DryStackExceptionsContainer"
import OperatingHoursInput from "./OperatingHoursInput"

const DryStackExceptionForm = ({ locations, refreshExceptionList }) => {
  const tracker = useTracker()
  const showToast = useToast()

  const {
    showExceptionForm,
    setShowExceptionForm,
    currentScheduleException,
    setCurrentScheduleException,
    showPartialAvailabilitySettings,
    setShowPartialAvailabilitySettings,
    editing,
    setEditing,
    trackingProps,
  } = useContext(DryStackExceptionsContext)

  const convertTimeRangesToFormFormat = (timeRanges) => {
    if (!timeRanges || timeRanges.length === 0) {
      return [{ startTime: "08:00", endTime: "18:00" }]
    }
    return timeRanges.map((range) => ({
      startTime: range[0],
      endTime: range[1],
    }))
  }

  const exceptionStartDate = currentScheduleException?.startDate
    ? parseISO(currentScheduleException?.startDate)
    : new Date()
  const exceptionEndDate = currentScheduleException?.endDate
    ? parseISO(currentScheduleException?.endDate)
    : new Date()
  const exceptionTimeRanges = convertTimeRangesToFormFormat(
    currentScheduleException?.overrideToAvailableTimeRanges
  )
  const exceptionMaxConcurrency =
    currentScheduleException?.overrideToMaximumEventConcurrency ?? null
  const exceptionAvailabilityType = currentScheduleException?.isBookable
    ? "partial"
    : "none"
  const exceptionLocations =
    currentScheduleException?.overrideToAvailableLocations
      ? currentScheduleException?.overrideToAvailableLocations.reduce(
          (hash, elem) => {
            hash[elem] = true
            return hash
          },
          {}
        )
      : locations.reduce((hash, elem) => {
          hash[elem] = true
          return hash
        }, {})
  const exceptionNote = currentScheduleException?.note ?? ""

  const methods = useForm({
    defaultValues: {
      exceptionStartDate,
      exceptionEndDate,
      exceptionTimeRanges,
      exceptionMaxConcurrency,
      exceptionAvailabilityType,
      exceptionLocations,
      exceptionNote,
    },
  })

  const {
    control,
    setValue,
    register,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = methods

  const handleSuccess = () => {
    resetAndClose()
    refreshExceptionList()
    showToast(
      `Exception date ${currentScheduleException ? "updated" : "saved"}!`,
      { type: "success" }
    )
  }

  const handleErrorState = (error) => {
    const errorMessage = Array.isArray(error.message)
      ? error.message.join(", ")
      : error.message

    if (errorMessage.includes("dates cannot overlap another override")) {
      setError(
        "exceptionStartDate",
        {
          type: "rangeOverlap",
          message:
            "Please select dates that do not overlap an existing exception.",
        },
        { shouldFocus: true }
      )
      setError("exceptionEndDate", { type: "rangeOverlap", message: "" })
    }
    if (errorMessage.includes("End date can not be before the start date")) {
      setError(
        "exceptionStartDate",
        {
          type: "invalidRange",
          message: "Exception start date must come before end date.",
        },
        { shouldFocus: true }
      )
      setError("exceptionEndDate", { type: "invalidRange", message: "" })
    }
    if (
      errorMessage.includes(
        "Availability must specify a String[][] time_ranges in valid %H:%M 24hr format"
      )
    ) {
      setError(
        "exceptionTimeRanges",
        {
          type: "required",
          message: "Please provide valid operating hours.",
        },
        { shouldFocus: true }
      )
    }
    if (errorMessage.includes("at least one override must be not nil")) {
      setError(
        "exceptionMaxConcurrency",
        {
          type: "required",
          message:
            "Operating hours and available time slots per window cannot both be blank.",
        },
        { shouldFocus: true }
      )
    }
    if (
      errorMessage.includes(
        "Start time must be before end time for each time range"
      )
    ) {
      setError(
        "exceptionTimeRanges",
        {
          type: "invalidRange",
          message: "Start time must come before end time for each time range.",
        },
        { shouldFocus: true }
      )
    }
    if (errorMessage.includes("time ranges cannot overlap")) {
      setError(
        "exceptionTimeRanges",
        {
          type: "overlap",
          message: "Operating hours cannot overlap.",
        },
        { shouldFocus: true }
      )
    }
  }

  const validateAtLeastOneLocationEnabled = (value, formValues) => {
    const locationCheckedValues = locations.map((location) => {
      return formValues.exceptionLocations[location]
    })

    if (locationCheckedValues.filter(Boolean).length === 0) {
      return "Please specify at least one launch location that is available."
    }
  }

  const createMutation = useMutation(
    (formState) => createScheduleException(getCurrentMarinaSlug(), formState),
    {
      onSuccess: handleSuccess,
      onError: handleErrorState,
    }
  )

  const updateMutation = useMutation(
    (formState) =>
      updateScheduleException(
        getCurrentMarinaSlug(),
        formState,
        currentScheduleException.id
      ),
    {
      onSuccess: handleSuccess,
      onError: handleErrorState,
    }
  )

  const getDisabledLocations = (locations) => {
    const disabledLocations = []

    Object.entries(locations).forEach(([k, v]) => {
      if (v === false) {
        disabledLocations.push(k)
      }
    })

    return disabledLocations
  }

  const convertTimeRangesToApiFormat = (timeRanges) => {
    if (!timeRanges || timeRanges.length === 0) return []
    return timeRanges.map((range) => [range.startTime, range.endTime])
  }

  const handleFormSubmit = (formData) => {
    const timeRanges = convertTimeRangesToApiFormat(
      formData.exceptionTimeRanges
    )

    let availability = null
    if (timeRanges.length > 0) {
      availability = JSON.stringify({ time_ranges: timeRanges })
    }

    const data = {
      start_date: formData.exceptionStartDate,
      end_date: formData.exceptionEndDate,
      availability_type: formData.exceptionAvailabilityType,
      availability,
      maximum_event_concurrency: formData.exceptionMaxConcurrency,
      disabled_locations: getDisabledLocations(formData.exceptionLocations),
      id: currentScheduleException?.id,
      note: formData.exceptionNote,
    }
    if (editing) {
      tracker.trackEvent("dry_stack_settings:exception_date_edited", {
        ...trackingProps,
      })
      updateMutation.mutate(data)
    } else {
      tracker.trackEvent("dry_stack_settings:exception_date_saved", {
        ...trackingProps,
      })
      createMutation.mutate(data)
    }
  }

  const resetAndClose = () => {
    setShowExceptionForm(false)
    setShowPartialAvailabilitySettings(false)
    setCurrentScheduleException(null)
    setEditing(false)
    createMutation.reset()
    reset()
  }

  const handleNoAvailabilitySelection = () => {
    setShowPartialAvailabilitySettings(false)
    setValue("exceptionTimeRanges", [{ startTime: "08:00", endTime: "18:00" }])
    setValue("exceptionMaxConcurrency", null)
    setValue(
      "exceptionLocations",
      locations.reduce((hash, elem) => {
        hash[elem] = true
        return hash
      }, {})
    )
  }

  const renderDateSelectors = () => {
    return (
      <>
        <div className="flex gap-3">
          <div>
            <Form.Label htmlFor="exceptionStartDate">Start date</Form.Label>
            <Controller
              control={control}
              name={"exceptionStartDate"}
              rules={{
                required: "Start date is required",
              }}
              render={({ field: { onChange, value } }) => (
                <Form.DatePicker
                  id={"exceptionStartDate"}
                  {...{ onChange, value }}
                  hasErrors={!!errors.exceptionStartDate}
                  minDate={new Date()}
                />
              )}
            />
            {errors.exceptionStartDate?.type === "required" && (
              <Form.Error>{errors.exceptionStartDate?.message}</Form.Error>
            )}
          </div>
          <div>
            <Form.Label htmlFor="exceptionEndDate">End date</Form.Label>
            <Controller
              control={control}
              name={"exceptionEndDate"}
              rules={{
                required: "End date is required",
              }}
              render={({ field: { onChange, value } }) => (
                <Form.DatePicker
                  id={"exceptionEndDate"}
                  {...{ onChange, value }}
                  hasErrors={!!errors.exceptionEndDate}
                  minDate={new Date()}
                />
              )}
            />
            {errors.exceptionEndDate?.type === "required" && (
              <Form.Error>{errors.exceptionEndDate?.message}</Form.Error>
            )}
          </div>
        </div>
        {errors.exceptionStartDate &&
          ["rangeOverlap", "invalidRange"].includes(
            errors.exceptionStartDate?.type
          ) && <Form.Error>{errors.exceptionStartDate?.message}</Form.Error>}
      </>
    )
  }

  const renderFormButtons = () => {
    return (
      <>
        <Button
          variant="tertiary"
          type="reset"
          onClick={resetAndClose}
          disabled={createMutation.isLoading || updateMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={createMutation.isLoading || updateMutation.isLoading}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        >
          {editing ? "Update" : "Save"} exception
        </Button>
      </>
    )
  }

  const renderAvailabilityTypeSelectors = () => {
    return (
      <>
        <div className="flex flex-nowrap items-center">
          <Form.Label htmlFor="availability-option-none">
            <div className="flex items-center">
              <input
                {...register("exceptionAvailabilityType")}
                className="mr-2 mt-0"
                id="availability-option-none"
                type="radio"
                value="none"
                onChange={() => handleNoAvailabilitySelection()}
              />
              <span className="font-normal">No availability</span>
            </div>
          </Form.Label>
        </div>
        <div className="flex flex-nowrap items-center">
          <Form.Label htmlFor="availability-option-partial">
            <div className="flex items-center">
              <input
                {...register("exceptionAvailabilityType")}
                id="availability-option-partial"
                className="mr-2 mt-0"
                type="radio"
                value="partial"
                onChange={() => setShowPartialAvailabilitySettings(true)}
              />
              <span className="font-normal">Available with exceptions</span>
            </div>
          </Form.Label>
        </div>
      </>
    )
  }

  const renderLocations = () => {
    return locations.map((location) => {
      return (
        <div
          className="mr-4 flex flex-nowrap items-center"
          key={`location-${location}`}
        >
          <Form.Label htmlFor={`location-option-${location}`}>
            <div className="flex items-center">
              <input
                {...register(`exceptionLocations.${location}`, {
                  validate: validateAtLeastOneLocationEnabled,
                })}
                id={`location-option-${location}`}
                className="mr-2 mt-0"
                type="checkbox"
              />
              {titlecase(location)}
            </div>
          </Form.Label>
        </div>
      )
    })
  }

  const renderPartialAvailabilitySettings = () => {
    return (
      <>
        <div className="col-span-12">
          <hr className="my-0" />
        </div>
        <div className="text-muted col-span-12">
          Make changes to the selections below to override default availability
          for these dates
        </div>
        {errors.exceptionMaxConcurrency?.type === "required" && (
          <div className="col-span-12">
            <Form.Error>{errors.exceptionMaxConcurrency?.message}</Form.Error>
          </div>
        )}
        <div className="col-span-6 flex gap-3">
          <div className="w-full gap-2">
            <Form.Label>Operating hours</Form.Label>
            <OperatingHoursInput name="exceptionTimeRanges" />
            {errors.exceptionTimeRanges && (
              <Form.Error>{errors.exceptionTimeRanges.message}</Form.Error>
            )}
          </div>
        </div>
        <div className="col-span-12 flex">
          <div>
            <Form.Label htmlFor={"exceptionMaxConcurrency"}>
              Available time slots per launch window
            </Form.Label>
            <Form.TextField
              {...register("exceptionMaxConcurrency", {
                min: {
                  value: 1,
                  message:
                    "If no time slots are available please choose the 'No availability' option.",
                },
              })}
              id={"exceptionMaxConcurrency"}
              type={"number"}
              inputmode={"numeric"}
              hasErrors={!!errors.exceptionMaxConcurrency}
            />
            {errors.exceptionMaxConcurrency?.type === "min" && (
              <Form.Error>{errors.exceptionMaxConcurrency?.message}</Form.Error>
            )}
          </div>
        </div>
        <div className="col-span-12">
          <Form.Label>Available launch locations</Form.Label>
          <div className="col-span-12 flex flex-col">
            <div className="flex">{renderLocations()}</div>
            {errors?.exceptionLocations && (
              <Form.Error>
                {Object.keys(errors.exceptionLocations).length ===
                  locations.length &&
                  "Please select at least one available location."}
              </Form.Error>
            )}
          </div>
        </div>
      </>
    )
  }

  return (
    <FormProvider {...methods}>
      <div className="mt-5">
        <div className="mt-8">
          {showExceptionForm && (
            <>
              {(createMutation.isError || updateMutation.isError) && (
                <div className="mt-8 font-bold text-red-500">
                  There was a problem{" "}
                  {createMutation.isError ? "creating" : "updating"} your
                  exception, please correct any issues highlighted below or
                  contact mayday@dockwa.com for further assistance.
                </div>
              )}
              <div className="mt-8">
                <Panel>
                  <Form
                    id="exceptionForm"
                    onSubmit={handleSubmit(handleFormSubmit)}
                  >
                    <div className="grid grid-cols-12 gap-x-4 gap-y-6">
                      <div className="col-span-6">{renderDateSelectors()}</div>
                      <div className="col-span-6 flex items-end justify-end gap-3">
                        {renderFormButtons()}
                      </div>
                      <div className="col-span-12 flex gap-3">
                        {renderAvailabilityTypeSelectors()}
                      </div>
                      {showPartialAvailabilitySettings &&
                        renderPartialAvailabilitySettings()}
                      <div className="col-span-12">
                        <Form.Label optional htmlFor="note">
                          <div className="flex gap-1">
                            <span>Boater Note</span>
                            <Tooltip
                              text="This note will appear when boaters try to schedule a launch on these dates"
                              placement="top"
                              variant="dark"
                            >
                              <i className="fa-solid fa-circle-info font-semibold text-gray-700" />
                            </Tooltip>
                          </div>
                        </Form.Label>
                        <Form.TextField
                          id="note"
                          type="text"
                          {...register("exceptionNote")}
                        />
                      </div>
                    </div>
                  </Form>
                </Panel>
              </div>
            </>
          )}
        </div>
      </div>
    </FormProvider>
  )
}

DryStackExceptionForm.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.string).isRequired,
  refreshExceptionList: PropTypes.func.isRequired,
}

export default DryStackExceptionForm
